import { useEffect, useMemo, useState, lazy } from "react";

import usePrecoRegraV2 from "~fetchs/usePrecoRegraV2";
import ContainerDaTabelaPrecoPrecoRegra from "~pages/PricesTableV2/components/Tabela/ContainerDaTabelaPrecoRegra";
import useModal from "~components/Modal/hooks/useModal";
import { initialPath } from "~misc/paths";
import { useExcluirPreco } from "~pages/PricesTableV2/hooks/usePricesTableQuery";
import LazyModalWrapper from "~pages/PricesTableV2/components/Modal/LazyModalWrapper";

const PricesTableSolicitarNegociacaoModal = lazy(() =>
  import("~pages/PricesTableV2/components/Modal/SolicitarNegociacaoModal")
);
const ExcluirModal = lazy(() => import("~pages/PricesTableV2/components/Modal/ExcluirModal"));
const ModalResponse = lazy(() => import("~components/Modal/ModalResponse"));
import FiltroPreco from "~pages/PricesTableV2/components/filters/FiltroPreco";
import { FormProvider, useForm } from "react-hook-form";
import { getStoragekeysRules } from "~pages/PricesTableV2/helpers/storage";
import { SaveIndexBD } from "~services/utils/SaveIndexBD";
import { useNavigate } from "react-router-dom";

const PriceRule = () => {
  const keys = useMemo(() => getStoragekeysRules("filtroPreco"), []);
  const navigate = useNavigate();
  const getDefaultValues = () => {
    try {
      const savedGrupoProduto = localStorage.getItem(keys.grupoProduto);
      const savedProduto = localStorage.getItem(keys.produto);
      const savedFilial = localStorage.getItem(keys.filiais);
      const savedMicro = localStorage.getItem(keys.micro);
      const savedSegmento = localStorage.getItem(keys.segmento);
      const savedSegmentoUnico = localStorage.getItem(keys.segmentoUnico);
      const savedCluster = localStorage.getItem(keys.cluster);
      const savedCidade = localStorage.getItem(keys.cidade);

      return {
        grupoProduto: savedGrupoProduto ? JSON.parse(savedGrupoProduto).value : 0,
        produto: savedProduto ? JSON.parse(savedProduto).value : 0,
        filial: savedFilial ? JSON.parse(savedFilial).value : 0,
        microrregiao: savedMicro ? JSON.parse(savedMicro).value : 0,
        grupoSegmento: savedSegmento ? JSON.parse(savedSegmento).value : 0,
        segmento: savedSegmentoUnico ? JSON.parse(savedSegmentoUnico).value : 0,
        cluster: savedCluster ? JSON.parse(savedCluster).value : 0,
        cidade: savedCidade ? JSON.parse(savedCidade).value : 0,
      };
    } catch (error) {
      console.warn("Error loading filters from localStorage:", error);
      return {
        grupoProduto: 0,
        produto: 0,
        filial: 0,
        microrregiao: 0,
        grupoSegmento: 0,
        segmento: 0,
        cluster: 0,
        cidade: 0,
      };
    }
  };

  const methods = useForm({
    defaultValues: getDefaultValues(),
  });

  const grupoProduto = methods.watch("grupoProduto");
  const produto = methods.watch("produto");
  const filial = methods.watch("filial");
  const microrregiao = methods.watch("microrregiao");
  const grupoSegmento = methods.watch("grupoSegmento");
  const segmento = methods.watch("segmento");
  const cluster = methods.watch("cluster");

  const cidade = methods.watch("cidade");

  const filters = useMemo(
    () => ({
      grupoProduto: grupoProduto || 0,
      produto: produto || 0,
      filial: filial || 0,
      microrregiao: microrregiao || 0,
      grupoSegmento: grupoSegmento || 0,
      segmento: segmento || 0,
      cluster: cluster || 0,
      cidade: cidade || 0,
    }),
    [grupoProduto, produto, filial, microrregiao, grupoSegmento, segmento, cluster, cidade]
  );

  const [onFiltros, setOnFiltros] = useState(false);
  const { isModalOpen, closeModal, setModalOpen } = useModal();
  const [dadosHeader, setDadosHeader] = useState({});
  const [loading, setLoading] = useState(false);
  const [skip, setSkip] = useState(0);
  const [take] = useState(10);

  const { precosRegra, total, filtrosDisponiveisPrecoRegra, totalRenderizado } = usePrecoRegraV2({
    filters: filters,
    setLoading,
    skip,
    take,
  });

  useEffect(() => {
    setOnFiltros({
      microrregiao: microrregiao > 0,
    });
  }, [microrregiao]);

  const handleAddButton = (grupoProdutoId, produtoId, filialId, clienteId, origem) => {
    const dadosHeader = { grupoProdutoId, produtoId, filialId, clienteId, origem };

    if (origem === "editar" && clienteId) {
      SaveIndexBD("dados-selecionados-negociacao", dadosHeader);
      navigate(`${initialPath}/tabela-preco/${clienteId}/editar-negociacao`);
      return;
    }

    setModalOpen(true);
    setDadosHeader(dadosHeader);
  };
  const {
    isModalOpen: isExcluirModalOpen,
    closeModal: closeExcluirModal,
    setModalOpen: setExcluirModalOpen,
  } = useModal();
  const [dadosDelete, setDadosDelete] = useState(null);
  const [responseDelete, setResponseDelete] = useState({
    isOpen: false,
    message: "Envio de exclusão feito com sucesso!",
    link: null,
  });

  const onSucessDeletar = (response) => {
    if (Array.isArray(response)) {
      const error = response.find((res) => res.status === "Error");
      if (error) {
        closeExcluirModal();
        setResponseDelete({ isOpen: true, message: error?.mensagem, link: null, status: "warning" });
        return;
      }
    }

    closeExcluirModal();
    setResponseDelete({
      isOpen: true,
      message: "Envio de exclusão feito com sucesso!",
      link: `${initialPath}/tabela-preco/`,
    });
  };

  const onErrorDeletar = () => {
    closeExcluirModal();
    setResponseDelete({
      isOpen: true,
      message: "Ocorreu um erro ao tentar deletar preço!",
      link: null,
      status: "warning",
    });
  };

  const excluirPreco = useExcluirPreco(dadosDelete, onSucessDeletar, onErrorDeletar);

  const handleDeleteButton = (precoId) => {
    setExcluirModalOpen(true);
    setDadosDelete(precoId);
  };

  const handleDelete = () => {
    excluirPreco();
  };

  return (
    <>
      <FormProvider {...methods}>
        <FiltroPreco
          filtrosDisponiveis={filtrosDisponiveisPrecoRegra}
          nomeDoFiltro={"filtroPreco"}
          filters={filters}
          loading={loading}
        />
      </FormProvider>

      <ContainerDaTabelaPrecoPrecoRegra
        handleAddButton={handleAddButton}
        handleDeleteButton={handleDeleteButton}
        filters={filters}
        onFiltros={onFiltros}
        estadoFiltro={0}
        total={total}
        precosRegra={precosRegra}
        loading={loading}
        setLoading={setLoading}
        setSkip={setSkip}
        take={take}
        totalRenderizado={totalRenderizado}
      />
      {isModalOpen && (
        <LazyModalWrapper>
          <PricesTableSolicitarNegociacaoModal
            isOpen={isModalOpen}
            closeModal={closeModal}
            dadosHeader={dadosHeader}
            filters={filters}
          />
        </LazyModalWrapper>
      )}

      {isExcluirModalOpen && (
        <LazyModalWrapper>
          <ExcluirModal
            isOpen={isExcluirModalOpen}
            onConfirm={handleDelete}
            mensagem={`Tem certeza de que deseja excluir este preço?`}
          />
        </LazyModalWrapper>
      )}

      {responseDelete?.isOpen && (
        <LazyModalWrapper>
          <ModalResponse
            isOpen={responseDelete?.isOpen}
            status={responseDelete?.status || "success"}
            message={responseDelete?.message || "Operação realizada com sucesso!"}
            closeModal={() => setResponseDelete({ isOpen: false, message: "", link: null })}
            link={responseDelete.link ? responseDelete.link : null}
          />
        </LazyModalWrapper>
      )}
    </>
  );
};

export default PriceRule;
